// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
/**
 * Client discovery (`GET /api/v1/client-config`, public). A client that knows only a server address — the mobile app pointed
 * at FlowZa Cloud or at an organisation's own on-premises server — learns from it where to sign in and whether it may talk to
 * this server at all. Everything in it is public by design: the auth URL and the anon / publishable key ship inside every web
 * bundle already and grant nothing on their own (the `anon` role holds no table privileges; RLS denies it every row).
 * Never add a secret, an internal host name or tenant data here.
 */
/** Where the server runs: FlowZa's hosted cloud, or an organisation's own infrastructure. */
export const DEPLOYMENT_MODES = ['cloud', 'on_prem'];
/** The `product` marker a client checks before trusting the document (any other JSON at that path is not a FlowZa server). */
export const CLIENT_CONFIG_PRODUCT = 'flowza-time';
/** Bumped only on a breaking change of the document; clients refuse a version newer than they understand. */
export const CLIENT_CONFIG_SCHEMA_VERSION = 1;
/** `1.4.0` style versions (the mobile app's `version`); a pre-release / build suffix is ignored by the comparison. */
export const appVersionSchema = z.string().trim().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}([-+][0-9A-Za-z.-]+)?$/, 'Expected a version such as 1.4.0');
export const clientConfigSchema = z.object({
    product: z.literal(CLIENT_CONFIG_PRODUCT),
    schemaVersion: z.number().int().min(1),
    /** The REST API version the server serves under `/api/<apiVersion>`. */
    apiVersion: z.string().min(1).max(10),
    deployment: z.enum(DEPLOYMENT_MODES),
    /** Shown on the sign-in screen so people can tell their organisation's server from FlowZa Cloud. */
    serverName: z.string().min(1).max(80),
    auth: z.object({
        /** Supabase Auth (GoTrue) — hosted, or self-hosted next to an on-premises API. */
        provider: z.literal('supabase'),
        /** Base URL the client signs in against (`<url>/auth/v1/…`), as reachable from the client's network. */
        url: z.string().url(),
        /** The publishable / anon key of that Auth server (public). */
        anonKey: z.string().min(1).max(2000),
    }),
    mobile: z.object({
        /** Oldest app version this server accepts; an older app asks the person to update. Null = any version. */
        minVersion: appVersionSchema.nullable(),
    }),
});
/**
 * Optional pointer a WEB host serves at `/.well-known/flowza-time.json` when the API lives on another host (FlowZa Cloud:
 * the web on time.flowza.ai, the API on time-api.flowza.ai), so people can enter the address they already know.
 */
export const clientDiscoveryPointerSchema = z.object({ product: z.literal(CLIENT_CONFIG_PRODUCT), apiUrl: z.string().url() });
//# sourceMappingURL=client-config.js.map